/* Hallmark · build · DcViewerPage · genre: modern-minimal · macrostructure: Workbench (viewer)
 * theme: Cobalt (project-locked) · pre-emit critique: P5 H5 E5 S5 R5 V5
 * nav: N3 side-rail (embedded in layout) · footer: none (app shell)
 * states: load · empty · populated · inspecting · searching
 * touch: all controls >= 44px · keyboard: Ctrl+K search focus, Escape dismiss drawer
 */

import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Menu, X, Edit3, History, ListChecks } from 'lucide-react';
import { FloorReviewPanel, type ReviewDraftOption } from '../components/editor/FloorReviewPanel';
import { api } from '../utils/api';
import { DcMapSVG } from '../components/calculator/DcMapSVG';
import { RackFlyoutDrawer, type RackDetail } from '../components/viewer/RackFlyoutDrawer';
import { useDcStore } from '../stores/dcStore';
import type { RackMeta } from '../hooks/useCalculatorLogic';
import { rackLabel } from '../utils/rackName.logic';
import { editorHref } from '../utils/floorPlan.logic';
import { getRackTemperature } from '../utils/thermalOverlay.logic';
import { useFloorAccess } from '../hooks/useDataCenterApi';
import { hasTelemetryReading, telemetryProvenance } from '../utils/telemetryProvenance.logic';
import type { WorkOrderFloorOverlay } from '../types/workOrderFloor';

// ─── Stat chip ─────────────────────────────────────────────────────────────────
function StatChip({ label, value, accent }: { label: string; value: string | number; accent?: string }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 1,
      padding: '8px 12px',
      background: 'rgba(255,255,255,0.03)',
      border: '1px solid rgba(255,255,255,0.07)',
      borderRadius: 7, minWidth: 70, flex: 1,
    }}>
      <span style={{ fontSize: 10, color: 'var(--text-muted, #6b7280)' }}>{label}</span>
      <span style={{ fontSize: 15, fontWeight: 700, color: accent || 'var(--text-primary, #e2e8f0)', fontFamily: 'JetBrains Mono, monospace' }}>
        {value}
      </span>
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────
export default function DcViewerPage() {
  const { code: dcCode } = useParams<{ code: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { datacenters, fetchDatacenters, loading: dcsLoading } = useDcStore();
  const dc = datacenters.find(d => d.code === dcCode);
  const { data: floorAccess } = useFloorAccess(dcCode ?? '');
  const demoMode = searchParams.get('demo') === '1';
  const workOrderId = searchParams.get('wo') ?? '';
  const stepId = searchParams.get('step') ?? '';
  const workOrders = useQuery<Array<{ id: string; title: string; status: string; items?: Array<{ metadata_json?: {
    rack_id?: number | string; source_rack_id?: number | string; target_rack_id?: number | string;
  } | null }> }>>({
    queryKey: ['floor-work-orders', dcCode],
    queryFn: async () => {
      const { data } = await api.get(`/api/datacenters/${dcCode}/impact-analysis/work-orders`);
      if (!Array.isArray(data)) throw new Error('Invalid work orders response');
      return data;
    },
    enabled: !!dcCode && floorAccess?.view !== false,
    retry: false,
  });
  const projection = useQuery<{ floor_overlay: WorkOrderFloorOverlay }>({
    queryKey: ['floor-work-order-overlay', dcCode, workOrderId, stepId],
    queryFn: () => api.get(`/api/datacenters/${dcCode}/racks/future-view`, { params: {
      as_of_work_order: workOrderId, ...(stepId ? { step_id: stepId } : {}),
    } }).then(result => result.data),
    enabled: !!dcCode && !!workOrderId && floorAccess?.view !== false,
    retry: false,
  });
  const overlay = workOrderId ? projection.data?.floor_overlay : undefined;
  const selectWorkOrder = (id: string, step = '') => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('wo', id); else next.delete('wo');
    if (step) next.set('step', step); else next.delete('step');
    setSearchParams(next);
  };

  const [searchQuery, setSearchQuery] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewDraft, setReviewDraft] = useState<{ id: number; revision: number }>();
  const [reviewDraftOptions, setReviewDraftOptions] = useState<ReviewDraftOption[]>();
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState('');
  const openDraftReview = async () => {
    setReviewLoading(true); setReviewError('');
    try {
      const drafts = (await api.get<Array<ReviewDraftOption & { published: boolean }>>(`/api/datacenters/${dcCode}/drafts`)).data;
      const saved = drafts.filter(draft => !draft.published);
      if (!saved.length) { setReviewError('Сохранённого неопубликованного черновика нет.'); return; }
      setReviewDraftOptions(saved); setReviewDraft(undefined); setReviewOpen(true);
    } catch { setReviewError('Не удалось загрузить сохранённый черновик. Повторите попытку.'); }
    finally { setReviewLoading(false); }
  };
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (datacenters.length === 0 && !dcsLoading) {
      fetchDatacenters();
    }
  }, [datacenters.length, fetchDatacenters, dcsLoading]);

  // Fetch rack list
  const { data: racksData, isLoading, refetch: refetchRacks } = useQuery<RackMeta[]>({
    queryKey: ['dc-viewer-racks', dcCode],
    queryFn: () => api.get(`/api/datacenters/${dcCode}/racks`).then((r: any) => r.data),
    enabled: !!dcCode,
  });

  // Fetch telemetry for thermal overlay
  const { data: telemetryData } = useQuery<any[]>({
    queryKey: ['dc-viewer-telemetry', dcCode, demoMode],
    queryFn: () => api.get(`/api/datacenters/${dcCode}/${demoMode ? 'telemetry-mock' : 'telemetry'}`).then((r: any) => r.data).catch(() => []),
    enabled: !!dcCode,
    refetchInterval: 30000,
  });

  const telemetryState = telemetryProvenance(telemetryData, demoMode);
  const telemetryForMap = hasTelemetryReading(telemetryData) ? telemetryData : null;

  const racks: RackMeta[] = racksData ?? [];
  const selectedRack = useMemo<RackDetail | null>(() => {
    const match = racks.find(rack => String(rack.id) === searchParams.get('rack'));
    return match ? { ...match, u_slots: match.u_slots ?? match.heightU } : null;
  }, [racks, searchParams]);

  // Filter by search
  const filteredRacks = useMemo(() => {
    if (!searchQuery.trim()) return racks;
    const q = searchQuery.toLowerCase();
    return racks.filter(r =>
      (r.label || '').toLowerCase().includes(q) ||
      String(r.row_index + 1).includes(q) ||
      (r.tenant || '').toLowerCase().includes(q)
    );
  }, [racks, searchQuery]);

  // Summary stats
  const stats = useMemo(() => {
    const total = racks.length;
    const blocked = racks.filter(r => r.is_blocked).length;
    return { total, blocked, active: total - blocked };
  }, [racks]);

  const handleRackClick = useCallback((r: RackMeta) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('rack', String(r.id));
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Keyboard shortcut Ctrl+K / Cmd+K to focus search
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSidebarOpen(true);
        setTimeout(() => {
          inputRef.current?.focus();
          inputRef.current?.select();
        }, 50);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const clearRackSelection = useCallback(() => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('rack');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    document.title = dc ? `${dc.name} — План зала | DC Cable Link` : 'План зала | DC Cable Link';
  }, [dc]);

  if (isLoading || dcsLoading || !dc) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-muted, #6b7280)', flexDirection: 'column', gap: 12 }}>
        <div style={{ width: 32, height: 32, border: '2px solid rgba(59,130,246,0.3)', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <span style={{ fontSize: 13 }}>Загрузка плана зала...</span>
        <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex', height: '100%',
      background: 'var(--bg-app, #0b0e14)',
      overflow: 'hidden',
      fontFamily: 'Inter, system-ui, sans-serif',
      position: 'relative',
    }}>
      {/* ── Left panel: info + controls ── */}
      {sidebarOpen && (
        <aside className="absolute inset-y-0 left-0 md:relative" style={{
          width: 280,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--bg-panel, #0d1117)',
          borderRight: '1px solid var(--border, #1a2744)',
          overflowY: 'auto',
          zIndex: 20,
        }}>
          {/* DC header */}
          <div style={{
            padding: '14px 16px 12px',
            borderBottom: '1px solid var(--border, #1a2744)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted, #6b7280)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>
                План зала ЦОД
              </div>
              <h1 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary, #e2e8f0)', margin: 0, lineHeight: 1.3 }}>
                {dc.name}
              </h1>
              <div style={{ fontSize: 11, color: 'var(--text-muted, #6b7280)', marginTop: 3 }}>
                {dc.code}
              </div>
            </div>

            {/* Mobile close sidebar button (>= 44px touch) */}
            <button
              onClick={() => setSidebarOpen(false)}
              className="md:hidden"
              aria-label="Скрыть боковую панель"
              style={{
                width: 44, height: 44,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'transparent', border: 'none',
                color: 'var(--text-muted, #6b7280)', cursor: 'pointer',
              }}
            >
              <X size={18} />
            </button>
          </div>

          {/* Stats */}
          <div style={{ padding: '8px 16px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 8 }}>
            <button className="editor-icon-button" style={{ width: 44, height: 44 }} title="История и сравнение ревизий" aria-label="История и сравнение ревизий" onClick={event => { event.currentTarget.focus(); setReviewDraft(undefined); setReviewDraftOptions(undefined); setReviewOpen(true); }}><History size={18} /></button>
            <button className="editor-icon-button" style={{ width: 44, height: 44 }} title="Review сохранённого черновика" aria-label="Review сохранённого черновика" disabled={reviewLoading} onClick={event => { event.currentTarget.focus(); void openDraftReview(); }}><ListChecks size={18} /></button>
            {reviewError && <span role="alert" style={{ fontSize: 12 }}>{reviewError}</span>}
          </div>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border, #1a2744)', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <StatChip label="Стойки" value={stats.total} />
            <StatChip label="Активных" value={stats.active} accent="#22c55e" />
            <StatChip label="Рядов" value={dc.rows_count} />
          </div>

          <div role="status" aria-label="Источник динамического слоя" style={{ padding: '8px 16px', borderBottom: '1px solid var(--border, #1a2744)', fontSize: 11, color: 'var(--text-muted, #6b7280)' }}>
            Динамический слой: <strong style={{ color: telemetryState === 'live' ? '#4ade80' : telemetryState === 'demo' ? '#fbbf24' : 'var(--text-secondary, #9ca3af)' }}>
              {telemetryState === 'live' ? 'LIVE' : telemetryState === 'demo' ? 'DEMO' : 'нет данных'}
            </strong>
            {telemetryState === 'demo' && <span> · только по `?demo=1`</span>}
          </div>

          <section aria-label="Слой наряда" style={{ padding: '12px 16px', borderBottom: '1px solid var(--border, #1a2744)', fontSize: 12 }}>
            <label htmlFor="floor-work-order">Наряд на плане</label>
            <select id="floor-work-order" value={workOrderId} onChange={event => selectWorkOrder(event.target.value)}
              style={{ display: 'block', width: '100%', minHeight: 44, marginTop: 6, background: 'var(--bg-input)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: 4 }}>
              <option value="">Только фактический план</option>
              {Array.isArray(workOrders.data) && workOrders.data.map(wo => <option key={wo.id} value={wo.id}>{wo.title}</option>)}
              {workOrderId && !workOrders.data?.some?.(wo => wo.id === workOrderId) && <option value={workOrderId}>Наряд {workOrderId.slice(0, 8)}</option>}
            </select>
            {workOrders.isError && <div role="alert">Не удалось загрузить список нарядов</div>}
            {workOrderId && projection.isLoading && <div role="status">Загрузка наряда...</div>}
            {workOrderId && projection.isError && <div role="alert">Наряд или шаг недоступен. Проверьте выбранный зал и права.</div>}
            {overlay && <>
              <label htmlFor="floor-work-order-step" style={{ display: 'block', marginTop: 10 }}>Шаг наряда</label>
              <select id="floor-work-order-step" value={stepId} onChange={event => selectWorkOrder(workOrderId, event.target.value)}
                style={{ width: '100%', minHeight: 44, marginTop: 6, background: 'var(--bg-input)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: 4 }}>
                <option value="">Весь наряд</option>
                {overlay.steps.map(step => <option key={step.id} value={step.id}>{step.sequence}. {step.description}</option>)}
              </select>
              <div role="status" style={{ marginTop: 8 }}>{overlay.status === 'MERGED' || overlay.status === 'CANCELLED' ? 'Наряд закрыт' : 'Плановые изменения'}</div>
              {!overlay.moves.length && !overlay.routes.length && <p>Нет незавершённых размещений или маршрутов.</p>}
              {overlay.moves.map(move => <div key={move.step_id} style={{ marginTop: 8, overflowWrap: 'anywhere' }}>
                <strong>{racks.find(rack => rack.id === move.rack_id)?.label ?? `Стойка #${move.rack_id ?? '?'}`} → {move.target_label ?? 'Без целевого адреса'}</strong>
                <div>{move.approved ? 'Согласован' : 'Не согласован'}</div>
                {move.conflict && <div role="alert" style={{ color: 'var(--status-critical, #ef4444)' }}>{move.conflict}</div>}
              </div>)}
              {overlay.routes.map((route, index) => <div key={route.cable_id ?? index} style={{ marginTop: 8 }}>
                {route.state === 'unavailable' ? <span role="alert">Маршрут недоступен: {route.reason}</span> : <>
                  <div>#{route.route_endpoints?.source.rack_id} U{route.route_endpoints?.source.u_slot} → #{route.route_endpoints?.target.rack_id} U{route.route_endpoints?.target.u_slot}</div>
                  <div>Установленный кабель: {route.installed_length_m ?? 'не задано'} м</div>
                  {route.length_conflict && <div role="alert">Длина подключения требует проверки</div>}
                </>}
              </div>)}
              <Link to={`/${dcCode}/tasks?wo=${encodeURIComponent(workOrderId)}`} style={{ display: 'inline-flex', alignItems: 'center', minHeight: 44 }}>Открыть наряд</Link>
            </>}
          </section>

          {/* Rack search (Ctrl+K) */}
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border, #1a2744)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted, #6b7280)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                Поиск стойки
              </span>
              <kbd style={{
                fontSize: 9, fontFamily: 'monospace',
                padding: '2px 5px', borderRadius: 4,
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.12)',
                color: 'var(--text-muted, #6b7280)',
              }}>
                Ctrl+K
              </kbd>
            </div>

            <div style={{ position: 'relative' }}>
              <input
                ref={inputRef}
                data-testid="viewer-rack-search"
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && filteredRacks.length > 0) {
                    handleRackClick(filteredRacks[0]);
                  }
                }}
                placeholder="Метка, ряд или арендатор..."
                style={{
                  width: '100%', boxSizing: 'border-box',
                  minHeight: 44,
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 6, padding: '10px 12px',
                  fontSize: 12, color: 'var(--text-primary, #e2e8f0)',
                  outline: 'none',
                }}
              />
            </div>

            {searchQuery && (
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted, #6b7280)', marginBottom: 6 }}>
                  Найдено: {filteredRacks.length} из {racks.length}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 180, overflowY: 'auto' }}>
                  {filteredRacks.slice(0, 10).map(r => (
                    <button
                      key={r.id}
                      data-testid={`search-result-rack-${r.id}`}
                      onClick={() => handleRackClick(r)}
                      aria-pressed={selectedRack?.id === r.id}
                      style={{
                        minHeight: 44,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 10px',
                        background: selectedRack?.id === r.id ? 'rgba(56,189,248,0.15)' : 'rgba(255,255,255,0.03)',
                        border: `1px solid ${selectedRack?.id === r.id ? 'rgba(56,189,248,0.4)' : 'rgba(255,255,255,0.06)'}`,
                        borderRadius: 6,
                        cursor: 'pointer',
                        textAlign: 'left',
                        color: 'var(--text-primary, #e2e8f0)',
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600 }}>
                          {rackLabel(r, dc)}
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--text-muted, #6b7280)' }}>
                          {r.address_confirmed === true ? `Ряд ${r.row_index + 1} · Поз. ${r.rack_index + 1}` : r.is_facility ? 'Инженерный объект' : 'Место не подтверждено'}
                        </div>
                      </div>
                      <span style={{ fontSize: 10, color: '#38bdf8', fontWeight: 500 }}>
                        Выбрать →
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Legend */}
          <div style={{ padding: '12px 16px', flex: 1, overflowY: 'auto' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted, #6b7280)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>
              Легенда
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {[
                { color: '#60a5fa', label: 'Лоток (трасса)' },
                { color: '#ef4444', label: 'Заблокировано / Вывод' },
                { color: '#38bdf8', label: 'Выбранный шкаф (фокус)' },
              ].map(item => (
                <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 10, height: 10, borderRadius: 2, background: item.color, flexShrink: 0 }} />
                  <span style={{ fontSize: 11, color: 'var(--text-muted, #6b7280)' }}>{item.label}</span>
                </div>
              ))}
            </div>

            {/* Temperature Legend (ASHRAE) */}
            {telemetryForMap && <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted, #6b7280)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>
                Температура (ASHRAE)
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {[
                  { color: '#0284c7', label: '< 25°C · Норма' },
                  { color: '#f59e0b', label: '25–30°C · Нагрев' },
                  { color: '#ef4444', label: '> 30°C · Перегрев' },
                ].map(item => (
                  <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 10, height: 10, borderRadius: 2, background: item.color, flexShrink: 0 }} />
                    <span style={{ fontSize: 11, color: 'var(--text-muted, #6b7280)' }}>{item.label}</span>
                  </div>
                ))}
              </div>
            </div>}
          </div>

          {/* Link to editor (>= 44px touch) */}
          {floorAccess?.edit && <div style={{ padding: '10px 16px', borderTop: '1px solid var(--border, #1a2744)' }}>
            <Link
              to={editorHref(dcCode!)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                minHeight: 44,
                padding: '10px 12px', borderRadius: 6,
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                color: 'var(--text-muted, #6b7280)', fontSize: 12,
                textDecoration: 'none',
              }}
            >
              <Edit3 size={14} />
              <span>Открыть редактор</span>
            </Link>
          </div>}
        </aside>
      )}

      {/* ── Main canvas area ── */}
      <main style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        <DcMapSVG
          key={dcCode}
          viewStateKey={`floor-view:${dcCode}:v1`}
          inspectorOpen={!!selectedRack}
          racks={filteredRacks}
          route={[]}
          onRackClick={(r: any) => handleRackClick(r as RackMeta)}
          dc={dc}
          focusedRackId={selectedRack?.id}
          onEmptyClick={clearRackSelection}
          telemetryMap={telemetryForMap}
          workOrderOverlay={overlay}
        />

        {/* Top-left bar: Sidebar toggle (on mobile) + Read-only badge */}
        <div style={{
          position: 'absolute', top: 12, left: 12,
          display: 'flex', alignItems: 'center', gap: 8,
          zIndex: 10,
        }}>
          {!sidebarOpen && (
            <button
              onClick={() => setSidebarOpen(true)}
              aria-label="Показать панель управления"
              style={{
                minWidth: 44, minHeight: 44,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(13,17,23,0.9)',
                border: '1px solid var(--border, #1a2744)',
                borderRadius: 8,
                color: 'var(--text-primary, #e2e8f0)',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
              }}
            >
              <Menu size={18} />
            </button>
          )}

          <div style={{
            minHeight: 32,
            display: 'flex', alignItems: 'center',
            padding: '4px 12px', borderRadius: 20,
            background: 'rgba(0,0,0,0.7)',
            border: '1px solid rgba(255,255,255,0.1)',
            fontSize: 11, color: 'var(--text-muted, #6b7280)',
            backdropFilter: 'blur(4px)',
            pointerEvents: 'none',
          }}>
            👁 Просмотр — без изменений
          </div>
        </div>

        {/* Slide-out Rack Inspector Drawer */}
        <RackFlyoutDrawer
          rack={selectedRack}
          dcCode={dcCode!}
          onClose={clearRackSelection}
          temperature={selectedRack ? getRackTemperature(selectedRack, telemetryForMap) : null}
          relatedWork={workOrders.data?.filter(wo => wo.items?.some(item =>
            [item.metadata_json?.rack_id, item.metadata_json?.source_rack_id, item.metadata_json?.target_rack_id]
              .some(id => id != null && String(id) === String(selectedRack?.id))))}
          workLoading={workOrders.isPending}
          workError={workOrders.isError}
        />
      </main>
      {reviewOpen && <FloorReviewPanel key={`${dcCode}:${reviewDraft?.id ?? 'history'}`} code={dcCode!} hallName={dc?.name} draft={reviewDraft} draftOptions={reviewDraftOptions} onDraftChange={setReviewDraft} onClose={() => { setReviewOpen(false); setReviewDraftOptions(undefined); }} onPublished={async () => { await fetchDatacenters(); await refetchRacks(); }} />}
    </div>
  );
}
